import React from 'react';

export function generateFacilityPin(facilityTypes: string[]) {
  const colorMap: Record<string, string> = {
    'consolidationFacility': '#F2A900',
    'cutFacility': '#D62D38',
    'distributionFacility': '#3ED3F2',
    'outboundFacility': '#F2A900',
    'warehouse': '#002F67'
  };

  // Check if Outbound is present for yellow border
  const hasOutbound = facilityTypes.includes('outboundFacility');
  const hasConsolidation = facilityTypes.includes('consolidationFacility');

  // Get colors for segments (exclude Outbound and Consolidation for coloring)
  const colors = facilityTypes
    .filter(type => type !== 'outboundFacility' && type !== 'consolidationFacility')
    .map(type => colorMap[type])
    .filter(Boolean);

  // If no colors and no outbound, default to gray
  if (colors.length === 0 && !hasOutbound) {
    colors.push('#008000');
  }

  const centerX = 18;
  const centerY = 17;
  const outerRadius = 13; // stays the same
  const innerRadius = hasOutbound ? 10 : 13; // smaller to avoid touching pin edge

  let segments = '';

  if (colors.length === 1) {
    // Single color - fill entire inner area
    const color = colors[0];
    segments = `
          <path d="M${centerX} ${centerY - innerRadius + 1}C${centerX + innerRadius - 1} ${centerY - innerRadius + 1} ${centerX + innerRadius - 1} ${centerY - 1} ${centerX + innerRadius - 1} ${centerY}
                   C${centerX + innerRadius - 1} ${centerY + innerRadius - 3} ${centerX} ${centerY + innerRadius + 8} ${centerX} ${centerY + innerRadius + 8}
                   C${centerX} ${centerY + innerRadius + 8} ${centerX - innerRadius + 1} ${centerY + innerRadius - 3} ${centerX - innerRadius + 1} ${centerY}
                   C${centerX - innerRadius + 1} ${centerY - 1} ${centerX - innerRadius + 1} ${centerY - innerRadius + 1} ${centerX} ${centerY - innerRadius + 1}Z" 
                fill="${color}" />`;
  } else if (colors.length === 2) {
    // Two colors - split vertically (left/right)
    const [color1, color2] = colors;
    segments = `
        <!-- Left half -->
        <path d="M${centerX} ${centerY - innerRadius + 1}C${centerX - innerRadius + 1} ${centerY - innerRadius + 1} ${centerX - innerRadius + 1} ${centerY - 1} ${centerX - innerRadius + 1} ${centerY}
                 C${centerX - innerRadius + 1} ${centerY + innerRadius - 3} ${centerX} ${centerY + innerRadius + 8} ${centerX} ${centerY + innerRadius + 8}
                 L${centerX} ${centerY} Z" fill="${color1}" />
        <!-- Right half -->
        <path d="M${centerX} ${centerY - innerRadius + 1}C${centerX + innerRadius - 1} ${centerY - innerRadius + 1} ${centerX + innerRadius - 1} ${centerY - 1} ${centerX + innerRadius - 1} ${centerY}
                 C${centerX + innerRadius - 1} ${centerY + innerRadius - 3} ${centerX} ${centerY + innerRadius + 8} ${centerX} ${centerY + innerRadius + 8}
                 L${centerX} ${centerY} Z" fill="${color2}" />`;
  } else if (colors.length >= 3) {
    // Three colors - your original design
    const [color1, color2, color3] = colors;
    segments = `
          <path d="M${centerX} ${centerY - innerRadius + 1}C${centerX + innerRadius - 1} ${centerY - innerRadius + 1} ${centerX + innerRadius - 1} ${centerY - 1} ${centerX + innerRadius - 1} ${centerY}
                   L${centerX} ${centerY} Z" fill="${color1}" />
          <path d="M${centerX} ${centerY} L${centerX + innerRadius - 1} ${centerY}
                   C${centerX + innerRadius - 1} ${centerY + innerRadius - 3} ${centerX} ${centerY + innerRadius + 8} ${centerX} ${centerY + innerRadius + 8}
                   L${centerX} ${centerY} Z" fill="${color2}" />
          <path d="M${centerX} ${centerY} L${centerX} ${centerY + innerRadius + 8}
                   C${centerX} ${centerY + innerRadius + 8} ${centerX - innerRadius + 1} ${centerY + innerRadius - 3} ${centerX - innerRadius + 1} ${centerY}
                   Z" fill="${color3}" />
          <path d="M${centerX - innerRadius + 1} ${centerY} C${centerX - innerRadius + 1} ${centerY - 1} ${centerX - innerRadius + 1} ${centerY - innerRadius + 1} ${centerX} ${centerY - innerRadius + 1}
                   L${centerX} ${centerY} Z" fill="${color1}" />`;
  }

  // Choose icon based on Consolidation
  const iconShape = hasConsolidation ?
    `<path d="M${centerX} 10 L19.9 16 H26 L21 19.9 L23 26 L${centerX} 22.5 L13 26 L15 19.9 L10 16 H16.1 L${centerX} 10Z" fill="white"/>` :
    `<circle cx="${centerX}" cy="${centerY}" r="4" fill="white"/>`;

  // Pin border - yellow if Outbound, otherwise gray
  const borderColor = hasOutbound ? `<path d="M${centerX} ${centerY - outerRadius + 1}C${centerX + outerRadius - 1} ${centerY - outerRadius + 1} ${centerX + outerRadius - 1} ${centerY - 1} ${centerX + outerRadius - 1} ${centerY}
  C${centerX + outerRadius - 1} ${centerY + outerRadius - 3} ${centerX} ${centerY + outerRadius + 8} ${centerX} ${centerY + outerRadius + 8}
  C${centerX} ${centerY + outerRadius + 8} ${centerX - outerRadius + 1} ${centerY + outerRadius - 3} ${centerX - outerRadius + 1} ${centerY}
  C${centerX - outerRadius + 1} ${centerY - 1} ${centerX - outerRadius + 1} ${centerY - outerRadius + 1} ${centerX} ${centerY - outerRadius + 1}Z" 
  fill="#F2A900" />` : <></>;

  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48" fill="none">
  <!-- Pin border -->
  ${borderColor}

  <!-- Colored segments -->
  ${segments}

  <!-- Icon (star or circle) -->
  ${iconShape}
  </svg>`;

  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(36, 48),
    anchor: new google.maps.Point(12, 17),
    labelOrigin: new google.maps.Point(14, 14)
  };

}